import { useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { trainingApi } from '../../api/training';
import { ApiError } from '../../api/client';
import { StatusBadge } from '../../components/StatusBadge';
import { DataTable } from '../../components/DataTable';
import { ReturnAction } from '../../components/ReturnAction';
import { ReturnedBanner } from '../../components/ReturnedBanner';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = {
  draft: 'Draft', submitted: 'Submitted', under_review: 'Under Review', returned: 'Returned for Redraft', approved: 'Approved',
  rejected: 'Rejected', completed: 'Completed', cancelled: 'Cancelled',
};

/** Same structure as MyLeavePage - includes a Supervisor Review section for direct reports, a person-specific check rather than role-based. */
export function MyTrainingPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  const catalogueQuery = useQuery({ queryKey: ['training-catalogue'], queryFn: () => trainingApi.catalogue() });
  const requestsQuery = useQuery({ queryKey: ['my-training'], queryFn: () => trainingApi.myRequests() });
  const pendingReviewQuery = useQuery({ queryKey: ['training-pending-supervisor'], queryFn: () => trainingApi.pendingForSupervisor() });

  const { register, handleSubmit, reset } = useForm<{ catalogueId?: string; trainingTitle?: string; trainingProvider?: string; reason?: string; proposedDate?: string }>();

  const requestMutation = useMutation({
    mutationFn: (values: { catalogueId?: string; trainingTitle?: string; trainingProvider?: string; reason?: string; proposedDate?: string }) =>
      trainingApi.request({ catalogueId: values.catalogueId ? Number(values.catalogueId) : undefined, trainingTitle: values.trainingTitle, trainingProvider: values.trainingProvider, reason: values.reason, proposedDate: values.proposedDate }),
    onSuccess: () => { showSuccess('Training request submitted.'); queryClient.invalidateQueries({ queryKey: ['my-training'] }); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to submit training request.'),
  });

  const decideMutation = useMutation({
    mutationFn: ({ id, decision }: { id: number; decision: 'approved' | 'rejected' }) => trainingApi.supervisorDecide(id, { decision }),
    onSuccess: () => { showSuccess('Decision recorded.'); queryClient.invalidateQueries({ queryKey: ['training-pending-supervisor'] }); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to record decision.'),
  });

  return (
    <div className="page">
      <Link to="/staff">&larr; Back to Dashboard</Link>
      <h1>My Training</h1>

      <section className="card">
        <h2>Request Training</h2>
        <form className="inline-form" onSubmit={handleSubmit((values) => requestMutation.mutate(values))}>
          <label>
            From Catalogue (optional)
            <select {...register('catalogueId')}>
              <option value="">- not from catalogue -</option>
              {catalogueQuery.data?.data.map((t) => <option key={t.id} value={t.id}>{t.title}</option>)}
            </select>
          </label>
          <label>Training Title<input {...register('trainingTitle')} /></label>
          <label>Provider<input {...register('trainingProvider')} /></label>
          <label>Proposed Date<input type="date" {...register('proposedDate')} /></label>
          <label>Reason<input {...register('reason')} /></label>
          <button type="submit" disabled={requestMutation.isPending}>{requestMutation.isPending ? 'Submitting…' : 'Request'}</button>
        </form>
      </section>

      <section className="card">
        <h2>My Requests</h2>
        {requestsQuery.data && (
          <DataTable
            rows={requestsQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No training requests yet."
            columns={[
              { header: 'Title', render: (r) => r.catalogue_title ?? r.training_title ?? '-' },
              { header: 'Provider', render: (r) => r.training_provider ?? '-' },
              { header: 'Proposed Date', render: (r) => r.proposed_date ?? '-' },
              { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
            ]}
          />
        )}
        {requestsQuery.data?.data.filter((r) => r.status === 'returned').map((r) => (
          <ReturnedBanner key={r.id} returnComment={r.return_comment} resubmit={() => trainingApi.resubmit(r.id)} queryKeysToInvalidate={[['my-training']]} />
        ))}
      </section>

      {pendingReviewQuery.data && pendingReviewQuery.data.data.length > 0 && (
        <section className="card">
          <h2>Awaiting Your Review (Direct Reports)</h2>
          <DataTable
            rows={pendingReviewQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage=""
            columns={[
              { header: 'Employee', render: (r) => r.employee_name },
              { header: 'Title', render: (r) => r.catalogue_title ?? r.training_title ?? '-' },
              { header: 'Reason', render: (r) => r.reason ?? '-' },
              {
                header: '',
                render: (r) => (
                  <span className="row-actions">
                    <button className="link-button" onClick={() => decideMutation.mutate({ id: r.id, decision: 'approved' })}>Approve</button>
                    <button className="link-button" onClick={() => decideMutation.mutate({ id: r.id, decision: 'rejected' })}>Reject</button>
                    <ReturnAction onReturn={(comment) => trainingApi.supervisorReturn(r.id, comment)} queryKeysToInvalidate={[['training-pending-supervisor']]} />
                  </span>
                ),
              },
            ]}
          />
        </section>
      )}
    </div>
  );
}
